import { z } from 'zod';
import {
  BareContentTemplateLegacySchema,
  ContentTemplateParamsSchema,
  FeedEditorElementType,
} from './contentTemplate';
import { objectIdSchema } from './objectId';
import { FeedContentWidgetType } from './const';

export const DimensionsSchema = z.object({
  width: z.number(),
  height: z.number(),
});

// Special dimensions type that can include units (e.g. 210mm)
export const DimensionsWithUnitsSchema = DimensionsSchema.extend({ units: z.string().optional() });

export const PositionSchema = z.object({
  x: z.number(),
  y: z.number(),
});

export const FeedContentType = [
  'rundown',
  'filtered-rundown',
  'msc-filtered-rundown',
  'static',
  'location-image',
  'date',
  'event',
  'venue',
  'venue-rundown',
  'venue-meal-periods',
  'column',
  'row',
  'wellness-rundown',
  'multi-day',
] as const;

export const FeedEditorClientRundown = ['vv-rundown', 'prd-rundown'] as const;

export const RectangleSchema = PositionSchema.and(DimensionsSchema);

export const FeedContentTypeSchema = z.enum(FeedContentType);
export const FeedEditorElementTypeSchema = z.enum(FeedEditorElementType);
export const FeedEditorClientRundownSchema = z.enum(FeedEditorClientRundown);
export const FeedContentWidgetTypeSchema = z.enum(FeedContentWidgetType);

export const ContentTypeSchema = z.union([
  FeedContentTypeSchema,
  FeedEditorElementTypeSchema,
  FeedEditorClientRundownSchema,
  FeedContentWidgetTypeSchema,
]);

export const BaseFeedAreaSchema = z.object({
  _id: objectIdSchema,
  name: z.string().optional(),
  rectangle: RectangleSchema,
  style: z.record(z.any()).optional(),
  className: z.string(),
  content: BareContentTemplateLegacySchema.optional(),
  contentType: ContentTypeSchema,
  params: ContentTemplateParamsSchema.optional(),
  isLocked: z.boolean().optional(),
});

type FeedArea = z.infer<typeof BaseFeedAreaSchema> & {
  children?: FeedArea[];
};

export const FeedAreaSchema: z.ZodType<FeedArea> = BaseFeedAreaSchema.extend({
  children: z.lazy(() => FeedAreaSchema.array().optional()),
}) as z.ZodType<FeedArea>;

export const BareFeedAreaSchema = z.object({
  _id: objectIdSchema,
  name: z.string().optional(),
  rectangle: RectangleSchema,
  style: z.record(z.any()).optional(),
  className: z.string(),
  content: z.string().optional(),
  contentType: ContentTypeSchema,
  params: ContentTemplateParamsSchema.optional(),
  children: FeedAreaSchema.array().optional(),
  isLocked: z.boolean().optional(),
});

export const FeedPageSchema = z.object({
  _id: objectIdSchema,
  image: z.string(),
  areas: FeedAreaSchema.array(),
  rundownFontSize: z.number().nullable().optional(),
  shouldSplit: z.boolean().optional(),
});

export const BareFeedPageSchema = z.object({
  _id: objectIdSchema,
  image: z.string(),
  rundownFontSize: z.number().nullable().optional(),
  areas: BareFeedAreaSchema.array(),
  shouldSplit: z.boolean().optional(),
});

export const FeedEditorFontSchema = z.object({
  name: z.string(),
  file: z.string(),
});

export const BareFeedTemplateSchema = z.object({
  _id: objectIdSchema,
  name: z.string(),
  property: objectIdSchema,
  dimensions: DimensionsSchema,
  dpi: z.number(),
  pages: BareFeedPageSchema.array(),
  renderedDimensions: DimensionsSchema.optional(),
  pdfDimensions: DimensionsWithUnitsSchema.optional(),
  fonts: FeedEditorFontSchema.array().optional(),
  // Whether the PDF background should be removed from the PDF
  removeBackground: z.boolean().optional(),
  // Must reference an scss file in styles/feeds, without the extension
  cssBundle: z.string().optional(),
  customAreasCss: z.string().optional(),
});

export const FeedTemplateSchema = z.object({
  _id: objectIdSchema,
  name: z.string(),
  property: objectIdSchema,
  dimensions: DimensionsSchema,
  dpi: z.number(),
  pages: FeedPageSchema.array(),
  renderedDimensions: DimensionsSchema.optional(),
  pdfDimensions: DimensionsWithUnitsSchema.optional(),
  fonts: FeedEditorFontSchema.array().optional(),
  // Whether the PDF background should be removed from the PDF
  removeBackground: z.boolean().optional(),
  cssBundle: z.string().optional(),
  customAreasCss: z.string().optional(),
});
